import React from "react";
import { Search, ScanLine, LoaderCircle, History } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ChainSelector } from "./ChainSelector";
import { validateTokenAddress } from "../lib/chainValidation";
import { shortAddr } from "../lib/utils";

const SOLANA_SUGGESTIONS = [
  { mint: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", symbol: "USDC" },
  { mint: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263", symbol: "BONK" },
  { mint: "7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr", symbol: "POPCAT" },
  { mint: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN", symbol: "JUP" },
];

const ROBINHOOD_SUGGESTIONS = [
  { mint: "0x322F0929c4625eD5bAd873c95208D54E1c003b2d", symbol: "TSLA" },
  { mint: "0x5a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b", symbol: "NVDA" },
];

export function MintScanner({
  onScan,
  scanning,
  onOpenRecents,
  recent = [],
  compact = false,
  chain,
  onChainChange,
}) {
  const [value, setValue] = React.useState("");
  const [error, setError] = React.useState("");

  // Clear input + error when chain changes
  React.useEffect(() => {
    setError("");
  }, [chain]);

  const submit = (e) => {
    e?.preventDefault?.();
    const v = value.trim();
    const err = validateTokenAddress(v, chain);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    onScan?.(v);
  };

  const fill = (mint) => {
    setValue(mint);
    setError("");
    onScan?.(mint);
  };

  const suggestions =
    chain === "robinhood" ? ROBINHOOD_SUGGESTIONS : SOLANA_SUGGESTIONS;

  const placeholder =
    chain === "solana"
      ? "Paste a Solana token mint address…"
      : "Paste a token contract address (0x…)";

  // Validate on each keystroke for inline feedback (only after first submit attempt)
  const handleChange = (e) => {
    setValue(e.target.value);
    if (error) {
      const err = validateTokenAddress(e.target.value.trim(), chain);
      setError(err || "");
    }
  };

  const isValid = !validateTokenAddress(value.trim(), chain);

  return (
    <form onSubmit={submit} className="w-full" data-testid="mint-scanner">
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Chain selector */}
        <ChainSelector
          value={chain}
          onChange={(c) => {
            onChainChange?.(c);
            setValue("");
            setError("");
          }}
          disabled={scanning}
        />

        {/* Token address input */}
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted))]"
          />
          <Input
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="none"
            className="pl-10"
            data-testid="mint-scanner-input"
          />
        </div>

        <Button
          type="submit"
          disabled={scanning || !value.trim()}
          size={compact ? "md" : "lg"}
          data-testid="mint-scanner-scan-button"
          className="min-w-[130px]"
        >
          {scanning ? (
            <>
              <LoaderCircle size={16} className="animate-spin" />
              Scanning
            </>
          ) : (
            <>
              <ScanLine size={16} />
              Scan
            </>
          )}
        </Button>

        {onOpenRecents ? (
          <Button
            type="button"
            variant="secondary"
            size={compact ? "md" : "lg"}
            onClick={onOpenRecents}
            data-testid="recent-scans-open-button"
            aria-label="Recent scans"
            className="sm:min-w-[120px]"
          >
            <History size={16} />
            <span className="hidden sm:inline">Recent</span>
            {recent?.length ? (
              <Badge variant="primary" className="px-1.5 py-0 text-[10px]">
                {recent.length}
              </Badge>
            ) : null}
          </Button>
        ) : null}
      </div>

      {error ? (
        <p
          className="mt-2 text-xs text-[hsl(var(--danger))]"
          data-testid="mint-scanner-error"
        >
          {error}
        </p>
      ) : null}

      {!compact ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[hsl(var(--muted))]">Try:</span>
          {suggestions.map((s) => (
            <button
              key={s.mint}
              type="button"
              onClick={() => fill(s.mint)}
              data-testid="mint-scanner-recent-pill"
              className="text-xs font-mono px-2.5 py-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] hover:border-[hsl(var(--primary)/0.5)] hover:text-[hsl(var(--primary))] transition-colors"
            >
              {s.symbol}
              <span className="ml-2 text-[hsl(var(--muted))]">
                {shortAddr(s.mint)}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </form>
  );
}
